import React, { useEffect, useState } from 'react'
import { Col, Container, Row, TopBar } from 'components'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import connectApi from 'api/connectApi'
import { EmptyList } from 'components/emptyList/EmptyList'
import { Spinner } from 'components/spinner/Spinner'
import { constants, getTodayDateString } from 'helpers'
import './enrollmentDashboard.scss'

const METABASE_EMBED_SCRIPT_ID = 'metabase-embed-script'

type EmbedConfig = {
    token: string
}

const loadEmbedScript = (instanceUrl: string) => {
    // Always refresh the config -- a later visit to this page (a different
    // village, in the same SPA session) must not reuse a stale instanceUrl.
    const windowWithMetabaseConfig = window as typeof window & {
        metabaseConfig?: unknown
    }
    windowWithMetabaseConfig.metabaseConfig = {
        theme: { preset: 'light' },
        isGuest: true,
        instanceUrl
    }

    if (document.getElementById(METABASE_EMBED_SCRIPT_ID)) {
        return
    }

    const script = document.createElement('script')
    script.id = METABASE_EMBED_SCRIPT_ID
    script.src = `${instanceUrl}/app/embed.js`
    script.defer = true
    document.body.appendChild(script)
}

export const EnrollmentDashboard = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { veVillageId = '', veCode = '' } = useParams()
    const [searchParams] = useSearchParams()
    const villageCode = searchParams.get('villageCode') || ''

    const [embedConfig, setEmbedConfig] = useState<EmbedConfig | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [isOffline, setIsOffline] = useState(!navigator.onLine)

    useEffect(() => {
        const updateOfflineStatus = () => setIsOffline(!navigator.onLine)
        window.addEventListener('online', updateOfflineStatus)
        window.addEventListener('offline', updateOfflineStatus)
        return () => {
            window.removeEventListener('online', updateOfflineStatus)
            window.removeEventListener('offline', updateOfflineStatus)
        }
    }, [])

    useEffect(() => {
        if (isOffline) {
            setLoading(false)
            return
        }

        if (!villageCode) {
            setError(t('enrollmentDashboard.error'))
            setLoading(false)
            return
        }

        setLoading(true)
        setError('')

        connectApi
            .get(constants.endpoints.METABASE_EMBED_TOKEN, {
                params: {
                    village_code: villageCode,
                    date: getTodayDateString()
                }
            })
            .then((response) => {
                if (response.status !== 200 || !response.data?.token) {
                    setError(t('enrollmentDashboard.error'))
                    return
                }

                loadEmbedScript(response.data.instance_url)
                setEmbedConfig({ token: response.data.token })
            })
            .catch(() => {
                setError(t('enrollmentDashboard.error'))
            })
            .finally(() => setLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [villageCode, isOffline])

    const handleBackNavigation = () => {
        navigate(
            veCode
                ? `/fc/village/${veVillageId}/profile/${veCode}`
                : `/fc/village/${veVillageId}/profile`
        )
    }

    return (
        <div className="page-wrapper">
            <Container>
                <Row>
                    <Col md={12}>
                        <TopBar back backNavigation={handleBackNavigation}>
                            {t('enrollmentDashboard.title')}
                        </TopBar>
                        <div className="enrollment-dashboard">
                            {loading && (
                                <Spinner
                                    size="90"
                                    pageSpinner={true}
                                    fullscreen={true}
                                />
                            )}
                            {!loading && isOffline && (
                                <EmptyList
                                    message={t('enrollmentDashboard.offline')}
                                />
                            )}
                            {!loading && !isOffline && error && (
                                <EmptyList message={error} />
                            )}
                            {!loading &&
                                !isOffline &&
                                !error &&
                                embedConfig && (
                                    <metabase-dashboard
                                        token={embedConfig.token}
                                        with-title="true"
                                        with-downloads="true"
                                    />
                                )}
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}
